import React from "react";

import {
  ArchiveRounded,
  ArrowForwardRounded,
  ContentCopyRounded,
  CreateRounded,
  DateRangeRounded,
  DeleteForeverRounded,
  LockClockRounded,
  MarkChatUnreadRounded,
  MoreVertRounded,
  OpenInNewRounded,
  RemoveRedEyeRounded,
  ScreenshotRounded,
  UnarchiveRounded,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useFirebase from "../../../../../config/context/firebaseContext";
import useMainState from "../../../../../config/context/mainStateContext";
import useModal from "../../../../../config/context/modalContext";
import { GreyPallet } from "../../../../../config/styles";
import { Dev } from "../../../../../config/util/consoleUtil";
import { UtilComponents } from "../../../../../config/util/utilComponents";

import MainBannerItem from "../../../../../components/MainConfigBanner/components/MainBannerItem";
import MainText from "../../../../../components/MainText";
import ModalWriteBanner from "../ModalWriteBanner";

import { CustomBox } from "./styles";

import { BannerItemType } from "../../../../../types/common";

type BannerItemProps = {
  item: BannerItemType;
  isActive: boolean;
  handleReload: () => void;
};

export default function BannerItem({ item, isActive, handleReload }: BannerItemProps) {
  const navigate = useNavigate();
  const { openLink } = useMainState();
  const { functions } = useFirebase();

  const { pushAlert } = useModal();

  const [loading, setLoading] = React.useState(false);

  const [showModalWrite, setShowModalWrite] = React.useState(false);

  const [anchorMenuItem, setAnchorMenuItem] = React.useState<null | HTMLElement>(null);
  const openMenuItem = Boolean(anchorMenuItem);

  const handleClickMenuItem = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMenuItem(event.currentTarget);
  };

  const handleCloseMenuItem = () => {
    setAnchorMenuItem(null);
  };

  const handleShowPreview = (item: BannerItemType) => {
    pushAlert({
      isClosable: true,
      title: "Preview",
      children: (handleClose) =>
        item !== null ? (
          <Box>
            <MainBannerItem
              item={item}
              collapseIn={true}
              onDimiss={() => {}}
              onActionClick={() => {
                item?.action?.type === "external-link"
                  ? openLink(String(item?.action?.content))
                  : item?.action?.type === "clipboard"
                  ? UtilComponents.copyToClipboard(
                      pushAlert,
                      item?.action?.content,
                      "Copiado para área de transferência!"
                    )
                  : navigate(String(item?.action?.content));
              }}
            />
            <MainText size="caption" color="regular">
              Id: {item.id}
            </MainText>
            {!!item.createdAtTimestamp && (
              <MainText size="caption" color="regular">
                Criação: {DateTime.fromMillis(item?.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
              </MainText>
            )}
            {!!item.updatedAtTimestamp && (
              <MainText size="caption" color="regular">
                Atualização:{" "}
                {DateTime.fromMillis(item?.updatedAtTimestamp).toFormat("dd/LL/y HH:mm")}
              </MainText>
            )}
            {!!item.archivedAtTimestamp && (
              <MainText size="caption" color="regular">
                Arquivado:{" "}
                {DateTime.fromMillis(item?.archivedAtTimestamp).toFormat("dd/LL/y HH:mm")}
              </MainText>
            )}
          </Box>
        ) : (
          <Box />
        ),
    });
  };

  const onCloseModalHandler = (reload?: boolean) => {
    setShowModalWrite(false);
    if (reload) handleReload();
  };

  //

  const handleWrite = async (type: "archive" | "unarchive") => {
    try {
      setLoading(true);
      const writeConfigBanner = httpsCallable(functions, "writeRemoteConfigBanner");

      const payload = { operation: type, id: item.id } as any;

      const response = await writeConfigBanner(payload);

      pushAlert({
        type: "success",
        message: (response?.data as any)?.message || "Banner atualizado",
      });

      handleReload();
    } catch (error) {
      console.log(...new Dev("handleWrite", error).Error());
      pushAlert({
        type: "error",
        message: `Erro ao salvar dados`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showModalWrite && <ModalWriteBanner onClose={onCloseModalHandler} banner={item} />}

      <Menu
        id="basic-menu"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorEl={anchorMenuItem}
        open={openMenuItem}
        onClose={handleCloseMenuItem}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        <Box sx={{ minWidth: "40vw", maxWidth: "60vw" }}>
          <MenuItem
            onClick={() => {
              handleShowPreview(item);
              handleCloseMenuItem();
            }}
          >
            <ListItemIcon>
              <RemoveRedEyeRounded fontSize="small" />
            </ListItemIcon>
            Ver
          </MenuItem>
          <MenuItem
            onClick={() => {
              item.archived ? handleWrite("unarchive") : handleWrite("archive");
              handleCloseMenuItem();
            }}
          >
            <ListItemIcon>
              {loading ? (
                <CircularProgress size={"16px"} color="inherit" />
              ) : item.archived ? (
                <UnarchiveRounded fontSize="small" />
              ) : (
                <ArchiveRounded fontSize="small" />
              )}
            </ListItemIcon>
            {item.archived ? "Desarquivar" : "Arquivar"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setShowModalWrite(true);
              handleCloseMenuItem();
            }}
          >
            <ListItemIcon>
              <CreateRounded fontSize="small" />
            </ListItemIcon>
            Editar
          </MenuItem>
        </Box>
      </Menu>

      <CustomBox key={item.id} sx={{ opacity: isActive ? 1 : 0.6 }}>
        <Box mt={1} mb={2} display={"flex"} justifyContent={"space-between"}>
          <Box gap={1} display={"flex"} flexWrap={"wrap"}>
            <Chip icon={<ScreenshotRounded />} label={item.screen} />

            <Chip icon={<MarkChatUnreadRounded />} color={item.type} label={item.type} />

            <Chip icon={<LockClockRounded />} label={item.persist ? "persist" : "not-persist"} />

            <Chip
              icon={<DeleteForeverRounded />}
              label={item.dimissable ? "dimissable" : "not-dimissable"}
            />

            <Chip
              icon={<DateRangeRounded />}
              label={
                item?.active
                  ? `${DateTime.fromMillis(item?.active.start).toFormat(
                      "dd/LL/y"
                    )} > ${DateTime.fromMillis(item?.active.end).toFormat("dd/LL/y")}`
                  : null
              }
            />

            {item.archived && <Chip icon={<ArchiveRounded />} label={"archived"} />}
          </Box>

          <Box>
            <IconButton disabled={loading} onClick={(event) => handleClickMenuItem(event)}>
              {loading ? <CircularProgress size={"24px"} color="inherit" /> : <MoreVertRounded />}
            </IconButton>
          </Box>
        </Box>

        <MainText size="body" color="regular">
          {item.text}
        </MainText>

        {item.action ? (
          <Box sx={{ lineBreak: "anywhere" }}>
            <Divider sx={{ my: 1 }} />

            <Box
              display={"flex"}
              justifyContent={"start"}
              alignItems={"center"}
              gap={1}
              color={GreyPallet.Regular}
            >
              {item?.action?.type === "external-link" ? (
                <OpenInNewRounded color="inherit" />
              ) : item?.action?.type === "clipboard" ? (
                <ContentCopyRounded color="inherit" />
              ) : (
                <ArrowForwardRounded color="inherit" />
              )}
              <Box>
                <MainText size="body" color="regular">
                  {item?.action?.text} ({item?.action?.type})
                </MainText>

                <MainText size="small" color="regular">
                  {item?.action?.content}
                </MainText>
              </Box>
            </Box>
          </Box>
        ) : null}
      </CustomBox>
    </>
  );
}
